import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { WorkspaceScopeProvider } from "@/contexts/WorkspaceScope.provider";
import { BusinessUnitProvider } from "@/contexts/BusinessUnit.provider";
import { LockedFocusProvider } from "@/contexts/LockedFocusContext";
import { HardModeProvider } from "@/contexts/HardModeContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieConsentGate } from "@/components/CookieConsentGate";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import About from "./pages/About";
import Help from "./pages/Help";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import DPA from "./pages/DPA";
import Support from "./pages/Support";
import Faq from "./pages/Faq";
import Changelog from "./pages/Changelog";
import Status from "./pages/Status";
import Today from "./pages/Today";
import Leads from "./pages/Leads";
import Pipeline from "./pages/Pipeline";
import Calendar from "./pages/Calendar";
import Results from "./pages/Results";
import Dashboard from "./pages/Dashboard";
import Insights from "./pages/Insights";
import Metrics from "./pages/Metrics";
import KeepWarm from "./pages/KeepWarm";
import Templates from "./pages/Templates";
import Learning from "./pages/Learning";
import Settings from "./pages/Settings";
import SettingsBilling from "./pages/SettingsBilling";
import Team from "./pages/Team";
import Integrations from "./pages/Integrations";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import IntegrationsMeta from "./pages/IntegrationsMeta";
import Assistant from "./pages/Assistant";
import AppLayout from "./components/layout/AppLayout";
import NotFound from "./pages/NotFound";
import ProductPage from "./pages/landing/ProductPage";
import PricingPage from "./pages/landing/PricingPage";
import UseCasesPage from "./pages/landing/UseCasesPage";
import WhyPage from "./pages/landing/WhyPage";
import SecurityPage from "./pages/landing/SecurityPage";
import Health from "./pages/Health";
import Contact from "./pages/Contact";
import Invite from "./pages/Invite";
import AcceptInvite from "./pages/AcceptInvite";

const queryClient = new QueryClient();

// CRITICAL: App boot logging (temporary, non-invasive)
// This helps track initialization order in production
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('[App] APP_BOOT_START');
}

const App = () => {
  // CRITICAL: Log app initialization milestones (temporary, non-invasive)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[App] APP_BOOT_OK - App component mounted');
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CookieConsentProvider>
          <WorkspaceScopeProvider>
            <BusinessUnitProvider>
              <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <CookieConsentGate />
                <LockedFocusProvider>
                  <HardModeProvider>
                    <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/about" element={<About />} />
            <Route path="/help" element={<Help />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/dpa" element={<DPA />} />
            <Route path="/support" element={<Support />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/status" element={<Status />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/how-it-works" element={<Navigate to="/#how-it-works" replace />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/use-cases" element={<UseCasesPage />} />
            <Route path="/why" element={<WhyPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/health" element={<Health />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Protected app routes */}
            <Route path="/app" element={<ProtectedRoute><AppLayout><Today /></AppLayout></ProtectedRoute>} />
            <Route path="/app/today" element={<ProtectedRoute><AppLayout><Today /></AppLayout></ProtectedRoute>} />
            <Route path="/app/leads" element={<ProtectedRoute><AppLayout><Leads /></AppLayout></ProtectedRoute>} />
            <Route path="/app/pipeline" element={<ProtectedRoute><AppLayout><Pipeline /></AppLayout></ProtectedRoute>} />
            <Route path="/app/calendar" element={<ProtectedRoute><AppLayout><Calendar /></AppLayout></ProtectedRoute>} />
            <Route path="/app/keep-warm" element={<ProtectedRoute><AppLayout><KeepWarm /></AppLayout></ProtectedRoute>} />
            <Route path="/app/templates" element={<ProtectedRoute><AppLayout><Templates /></AppLayout></ProtectedRoute>} />
            <Route path="/app/campaigns" element={<ProtectedRoute><Navigate to="/app/leads" replace /></ProtectedRoute>} />
            <Route path="/app/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
            <Route path="/app/insights" element={<ProtectedRoute><AppLayout><Insights /></AppLayout></ProtectedRoute>} />
            <Route path="/app/results" element={<ProtectedRoute><AppLayout><Results /></AppLayout></ProtectedRoute>} />
            {process.env.NODE_ENV === 'development' && (
              <Route path="/app/metrics" element={<ProtectedRoute><AppLayout><Metrics /></AppLayout></ProtectedRoute>} />
            )}
            <Route path="/app/learning" element={<ProtectedRoute><AppLayout><Learning /></AppLayout></ProtectedRoute>} />
            <Route path="/app/assistant" element={<ProtectedRoute><AppLayout><Assistant /></AppLayout></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
            <Route path="/app/settings/workspace" element={<ProtectedRoute><Navigate to="/app/settings" replace /></ProtectedRoute>} />
            <Route path="/app/settings/team" element={<ProtectedRoute><AppLayout><Team /></AppLayout></ProtectedRoute>} />
            <Route path="/app/settings/teams" element={<ProtectedRoute><Navigate to="/app/settings/team" replace /></ProtectedRoute>} />
            <Route path="/app/settings/integrations" element={<ProtectedRoute><Navigate to="/app/settings/integrations/meta" replace /></ProtectedRoute>} />
            <Route path="/app/settings/integrations/meta" element={<ProtectedRoute><AppLayout><IntegrationsMeta /></AppLayout></ProtectedRoute>} />
            <Route path="/app/settings/billing" element={<ProtectedRoute><AppLayout><SettingsBilling /></AppLayout></ProtectedRoute>} />
            <Route path="/app/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
            <Route path="/app/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>} />
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
                  </Routes>
                </HardModeProvider>
              </LockedFocusProvider>
            </BrowserRouter>
          </TooltipProvider>
          </BusinessUnitProvider>
        </WorkspaceScopeProvider>
          </CookieConsentProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
